import { useState, useCallback, useEffect } from 'react';

/**
 * Field selection preferences for BuildHub fields to share in enquiry.
 */
export interface BuildHubFieldSelection {
    /** Always true (locked) - title is always included */
    includeTitle: boolean;
    /** Whether to include location information */
    includeLocation: boolean;
    /** Whether to show full address (false = region only) */
    includeFullAddress: boolean;
    /** Whether to include budget information */
    includeBudget: boolean;
    /** Whether to include start date */
    includeStartDate: boolean;
    /** Whether to include end date */
    includeEndDate: boolean;
    /** Whether to include project scope */
    includeScope: boolean;
    /** Whether to include project stage */
    includeStage: boolean;
}

/**
 * Default field selection values.
 */
const DEFAULT_FIELD_SELECTION: BuildHubFieldSelection = {
    includeTitle: true,
    includeLocation: true,
    includeFullAddress: false,
    includeBudget: false,
    includeStartDate: true,
    includeEndDate: true,
    includeScope: true,
    includeStage: true,
};

/**
 * Get localStorage key for a specific BuildHub.
 */
const getStorageKey = (buildHubId: string): string => {
    return `buildHubFieldSelection_${buildHubId}`;
};

/**
 * Hook to manage BuildHub field selection state with localStorage persistence.
 *
 * @param buildHubId - The ID of the BuildHub project
 * @returns Tuple containing [fieldSelection, setFieldSelection]
 */
export function useBuildHubFieldSelection(
    buildHubId: string | undefined
): [BuildHubFieldSelection, (selection: BuildHubFieldSelection) => void] {
    const [fieldSelection, setFieldSelectionState] =
        useState<BuildHubFieldSelection>(DEFAULT_FIELD_SELECTION);

    // Load from localStorage on mount or when buildHubId changes
    useEffect(() => {
        if (!buildHubId) {
            setFieldSelectionState(DEFAULT_FIELD_SELECTION);
            return;
        }

        try {
            const stored = localStorage.getItem(getStorageKey(buildHubId));
            if (stored) {
                const parsed = JSON.parse(stored) as BuildHubFieldSelection;
                // Ensure includeTitle is always true (locked)
                setFieldSelectionState({
                    ...parsed,
                    includeTitle: true,
                });
            } else {
                setFieldSelectionState(DEFAULT_FIELD_SELECTION);
            }
        } catch (error) {
            console.error(
                'Failed to load field selection from localStorage:',
                error
            );
            setFieldSelectionState(DEFAULT_FIELD_SELECTION);
        }
    }, [buildHubId]);

    // Save to localStorage when field selection changes
    const setFieldSelection = useCallback(
        (selection: BuildHubFieldSelection) => {
            // Ensure includeTitle is always true (locked)
            const normalizedSelection: BuildHubFieldSelection = {
                ...selection,
                includeTitle: true,
            };

            setFieldSelectionState(normalizedSelection);

            if (buildHubId) {
                try {
                    localStorage.setItem(
                        getStorageKey(buildHubId),
                        JSON.stringify(normalizedSelection)
                    );
                } catch (error) {
                    console.error(
                        'Failed to save field selection to localStorage:',
                        error
                    );
                }
            }
        },
        [buildHubId]
    );

    return [fieldSelection, setFieldSelection];
}
